// Builds the full HTML body for the customer registration email.
// The output is meant to be sent as the `html_content` variable in a
// pass-through EmailJS template whose body is exactly: {{{html_content}}}

const escapeHtml = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatDate = (d) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

const formatTime = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const fieldRow = (label, value, isStrong = false, isLink = false) => `
  <tr>
    <td style="width:40%;padding:7px 10px 7px 0;font-size:11px;letter-spacing:0.14em;color:#9aa3af;text-transform:uppercase;vertical-align:top;">
      ${label}
    </td>
    <td style="padding:7px 0;font-size:14px;color:#e5e7eb;${
      isStrong ? "color:#ffffff;font-weight:bold;" : ""
    }">
      ${
        isLink
          ? `<a href="mailto:${value}" style="color:#7cc4ff;text-decoration:underline;">${value}</a>`
          : value || "&mdash;"
      }
    </td>
  </tr>`;

const sectionCard = (title, rowsHtml) => `
  <tr>
    <td style="padding:0 24px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #4a5160;border-radius:8px;overflow:hidden;">
        <tr>
          <td style="background-color:#2e6e98;padding:12px 20px;">
            <div style="font-size:12px;letter-spacing:0.18em;color:#ffffff;text-transform:uppercase;font-weight:bold;">
              ${title}
            </div>
          </td>
        </tr>
        <tr>
          <td style="padding:18px 20px 8px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              ${rowsHtml}
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr><td style="height:20px;line-height:20px;">&nbsp;</td></tr>`;

export function buildEmailHtml(data) {
  const e = escapeHtml;
  const now = new Date();

  const companyName = e(data.company_name);
  const vatNumber = e(data.vat_number);
  const emailAddr = e(data.email);
  const phone = e(data.phone);
  const address = e(data.address);
  const capCity = `${e(data.cap)} &mdash; ${e(data.city)}`;
  const country = data.country
    ? `${e(data.country.name)} (${e(data.country.code)})`
    : "";
  const paymentTerms = e(data.payment_terms);
  const currency = data.currency
    ? `${e(data.currency.code)} &mdash; ${e(data.currency.name)}`
    : "";
  const salesManagerName = e(data.sales_manager_name);
  const salesManagerPhone = e(data.sales_manager_phone);

  const datiAziendali = [
    fieldRow("Ragione Sociale", companyName, true),
    fieldRow("Partita IVA", vatNumber),
    fieldRow("Telefono", phone),
    fieldRow("Email", emailAddr, false, true),
    fieldRow("Responsabile Commerciale", salesManagerName),
    fieldRow("Telefono Resp. Commerciale", salesManagerPhone),
  ].join("");

  const sedeLegale = [
    fieldRow("Indirizzo", address),
    fieldRow("CAP / Citt&agrave;", capCity),
    fieldRow("Paese", country),
  ].join("");

  const terminiCommerciali = [
    fieldRow("Termini di Pagamento", paymentTerms),
    fieldRow("Valuta", currency),
  ].join("");

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Nuova Registrazione Clienti</title>
</head>
<body style="margin:0;padding:0;background-color:#3a4148;font-family:Arial,Helvetica,sans-serif;color:#e5e7eb;-webkit-font-smoothing:antialiased;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#3a4148;padding:32px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background-color:#2c323a;border-radius:10px;overflow:hidden;border:1px solid #4a5160;">

          <tr>
            <td style="background-color:#2e6e98;padding:28px 32px 24px;text-align:center;">
              <div style="font-size:11px;letter-spacing:0.22em;color:#cfe3f4;margin-bottom:8px;text-transform:uppercase;font-weight:bold;">
                HYDROTRADE
              </div>
              <h1 style="margin:0;font-size:24px;font-weight:bold;color:#ffffff;line-height:1.2;">
                Nuova Registrazione Clienti
              </h1>
              <div style="margin-top:10px;font-size:13px;color:#cfe3f4;">
                ${companyName || "&mdash;"} &middot; ${formatDate(now)} &middot; ${formatTime(now)}
              </div>
            </td>
          </tr>

          <tr>
            <td style="padding:26px 32px 8px;">
              <p style="margin:0;font-size:15px;line-height:1.55;color:#d6dbe2;">
                &Egrave; stata ricevuta una nuova richiesta di registrazione anagrafica. Trova qui di seguito tutti i dati inviati dal cliente.
              </p>
            </td>
          </tr>

          <tr><td style="height:24px;line-height:24px;">&nbsp;</td></tr>

          ${sectionCard("Dati Aziendali", datiAziendali)}
          ${sectionCard("Sede Legale", sedeLegale)}
          ${sectionCard("Termini Commerciali", terminiCommerciali)}

          <tr>
            <td style="padding:0 24px 24px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#1f5b7d20;border:1px solid #2e6e98;border-radius:6px;">
                <tr>
                  <td style="padding:12px 16px;font-size:12px;color:#cfe3f4;line-height:1.5;">
                    &#x2713; Il cliente ha accettato il trattamento dei dati personali ai sensi del GDPR (Reg. UE 2016/679).
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="background-color:#262b32;padding:18px 32px;text-align:center;font-size:11px;color:#9aa3af;border-top:1px solid #4a5160;">
              Email automatica generata dal modulo di registrazione del sito Hydrotrade.<br>
              Non rispondere a questo messaggio.
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
